/**
 * LiveToastListener — Phase 18c.
 *
 * Mounts at the layout root. Subscribes to the notification
 * WebSocket and pops a ``react-hot-toast`` for each incoming
 * event. The bell in the topbar has its OWN socket subscription
 * (for the unread badge + dropdown preview) — that's fine;
 * :class:`Hub` fans out to every connection so both paths see
 * the same payload.
 *
 * Toast behaviour:
 *
 *   * URGENT  → red error toast, 10s
 *   * HIGH    → amber icon, 7s
 *   * NORMAL  → default, 5s
 *   * LOW     → muted, 3s
 *
 * Clicking the toast navigates to /notifications. De-duplicates
 * on id so a brief reconnect burst doesn't fire the same toast
 * twice.
 */
import React, { useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiBell, FiInfo } from 'react-icons/fi';
import useNotificationSocket from '../../hooks/useNotificationSocket';

const PRIORITY_CONFIG = {
  URGENT: { duration: 10000, icon: <FiAlertCircle color="#dc3545" />, bg: '#fef2f2' },
  HIGH: { duration: 7000, icon: <FiAlertCircle color="#f59e0b" />, bg: '#fffbeb' },
  NORMAL: { duration: 5000, icon: <FiBell color="#2563eb" />, bg: '#eff6ff' },
  LOW: { duration: 3000, icon: <FiInfo color="#6b7280" />, bg: '#f9fafb' },
};

const LiveToastListener = () => {
  // De-dupe within a short window — if the socket reconnects and
  // the server re-sends a payload we already showed, swallow it.
  const recentIds = useRef(new Set());

  const handler = useMemo(() => (notification) => {
    if (!notification?.id || recentIds.current.has(notification.id)) return;
    recentIds.current.add(notification.id);
    // Evict after 60s so we don't hoard ids forever.
    setTimeout(() => recentIds.current.delete(notification.id), 60000);

    const cfg = PRIORITY_CONFIG[notification.priority] || PRIORITY_CONFIG.NORMAL;
    const title = notification.title || notification.subject || notification.type;
    const body = notification.body || '';

    toast.custom((t) => (
      <Link
        to="/notifications"
        onClick={() => toast.dismiss(t.id)}
        className="text-decoration-none text-reset"
        style={{
          display: 'block',
          background: cfg.bg,
          padding: '0.75rem 1rem',
          borderRadius: 8,
          minWidth: 280, maxWidth: 420,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.06)',
          pointerEvents: 'auto',
          opacity: t.visible ? 1 : 0,
          transition: 'opacity 150ms ease',
        }}
      >
        <div className="d-flex align-items-start">
          <div className="me-2" style={{ paddingTop: 2 }}>
            {cfg.icon}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="fw-semibold small text-truncate">{title}</div>
            {body && (
              <div className="small text-muted" style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}>
                {body}
              </div>
            )}
          </div>
        </div>
      </Link>
    ), { duration: cfg.duration });
  }, []);

  useNotificationSocket({ onNotification: handler });
  return null;
};

export default LiveToastListener;
